/**
 * Knowledge of Language and Life. At settlement both limbs must be met: the
 * Life in the UK test AND English at the level for the stage. They are
 * separate requirements that interact — passing the test does not remove the
 * language requirement, and an age or medical exemption removes both.
 */
import type { EvaluatePredicate } from "./predicate.js";

export const life_in_uk_test: EvaluatePredicate = (facts) => {
  if (facts.koll.exempt_age_or_medical === true) {
    return {
      status: "NOT_APPLICABLE",
      notes: ["An exemption on age or medical grounds has been indicated."],
    };
  }
  if (facts.koll.life_in_uk_passed === null) {
    return { status: "PENDING_EVIDENCE", notes: ["Whether the Life in the UK test has been passed is needed."] };
  }
  return facts.koll.life_in_uk_passed
    ? { status: "MET" }
    : { status: "NOT_MET", notes: ["The rules ask for a pass in the Life in the UK test at this stage."] };
};

/**
 * The interaction, stated explicitly because it is a common and expensive
 * misunderstanding: KoLL is not satisfied by the test alone.
 */
export const koll_interaction: EvaluatePredicate = (facts, _rule, ctx) => {
  if (facts.koll.exempt_age_or_medical === true) {
    return { status: "NOT_APPLICABLE", notes: ["An exemption on age or medical grounds has been indicated."] };
  }

  const test = ctx.results.get("KOLL.TEST");
  const english = [...ctx.results.values()].find(
    (r) => r.rule_id.startsWith("ENG.ILR") && r.status !== "NOT_APPLICABLE",
  );

  const statuses = [test?.status, english?.status];
  if (statuses.includes("NOT_MET")) {
    return {
      status: "NOT_MET",
      because: ["KOLL.TEST"],
      notes: ["Both limbs are needed at settlement: the Life in the UK test and English at the required level."],
    };
  }
  if (statuses.includes("PENDING_EVIDENCE") || statuses.includes(undefined)) {
    return {
      status: "PENDING_EVIDENCE",
      because: ["KOLL.TEST"],
      notes: ["Both limbs are needed at settlement: the Life in the UK test and English at the required level."],
    };
  }
  return { status: "MET", because: ["KOLL.TEST"] };
};
